// Data Health API - CRM Data Quality & Automation Alerts
import { baseClient } from './baseClient';

// Types
export interface DataHealthScore {
  overall_score: number;
  contact_completeness: number;
  deal_freshness: number;
  task_management: number;
  activity_tracking: number;
  grade: string;
}

export interface StaleRecord {
  id: string;
  name: string;
  record_type: string;
  days_stale: number;
  last_activity: string | null;
  suggested_action: string;
}

export interface MissingInfoRecord {
  id: string;
  name: string;
  record_type: string;
  missing_fields: string[];
  completeness_percent: number;
}

export interface ActionableNudge {
  id: string;
  type: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  record_id: string;
  record_type: string;
  suggested_action: string;
  action_url: string;
}

export interface DataHealthDashboard {
  score: DataHealthScore;
  stale_deals: StaleRecord[];
  forgotten_contacts: StaleRecord[];
  missing_info_contacts: MissingInfoRecord[];
  overdue_tasks: number;
  nudges: ActionableNudge[];
  stats: {
    total_contacts: number;
    total_deals: number;
    total_tasks: number;
    overdue_tasks: number;
    stale_deals_count: number;
    forgotten_contacts_count: number;
    incomplete_contacts_count: number;
  };
}

// API Functions
export const dataHealthApi = {
  // Get overall data health score
  getScore: async (): Promise<DataHealthScore> => {
    return baseClient.get<DataHealthScore>('/data-health/score');
  },

  // Get stale deals
  getStaleDeals: async (days: number = 14, limit: number = 10): Promise<StaleRecord[]> => {
    return baseClient.get<StaleRecord[]>(`/data-health/stale-deals?days=${days}&limit=${limit}`);
  },

  // Get forgotten contacts
  getForgottenContacts: async (days: number = 30, limit: number = 10): Promise<StaleRecord[]> => {
    return baseClient.get<StaleRecord[]>(`/data-health/forgotten-contacts?days=${days}&limit=${limit}`);
  },

  // Get contacts with missing info
  getMissingInfo: async (limit: number = 10): Promise<MissingInfoRecord[]> => {
    return baseClient.get<MissingInfoRecord[]>(`/data-health/missing-info?limit=${limit}`);
  },

  // Get actionable nudges
  getNudges: async (limit: number = 5): Promise<ActionableNudge[]> => {
    return baseClient.get<ActionableNudge[]>(`/data-health/nudges?limit=${limit}`);
  },

  // Get full data health dashboard
  getDashboard: async (): Promise<DataHealthDashboard> => {
    return baseClient.get<DataHealthDashboard>('/data-health/dashboard');
  },

  // Get duplicate contacts
  getDuplicateContacts: async (limit: number = 10): Promise<{ duplicates: any[]; total_found: number }> => {
    return baseClient.get(`/data-health/duplicate-contacts?limit=${limit}`);
  },
};

export default dataHealthApi;
